<template>
    <div class="payment__form">
        <form
            v-if="state.currentView == 'MPESA_INIT_VIEW' && state.processing != true"
            @submit.prevent="checkPhoneAndNetwork"
        >
            <h5
                class="container__col-md-12 container__col-sm-12 payment__instruction"
            >
                {{$t("mpesa.init.instruction")}}
            </h5>
            <primary-select
                :title="$t('mpesa.input.network')"
                :description-text="$t('ui.input.choose')"
                @value-change="network = $event"
                :options="state.networks"
                :defaultValue="state.defaultNetwork"
                :isRequired="getValueFromPath(state, 'inputs.network.required') || true"
                inputName="network"
            />
            <primary-input
                type="tel"
                :title="$t('mpesa.input.phone')"
                class="container__col-md-12 container__col-sm-12"
                prefix-image="smart-phone"
                placeholder="07013926478"
                @value-change="telephone = $event"
                :defaultValue="getValueFromPath(state, 'inputs.phone.value') || null"
                :isRequired="getValueFromPath(state, 'inputs.phone.required') || true"
                inputName="phone"
                :number-only="true"
            />
            <PrimaryButton
                :content="
                    [$t('ui.pay.btn'), state.currency, state.amount.toLocaleString()]
                        .filter(Boolean)
                        .join(' ')
                "
                type="submit"
            />
        </form>
        <div
            v-else-if="
                state.currentView == 'MPESA_CONFIRM_VIEW' && state.processing != true
            "
        >
            <div :class="[state.fallbackDetails != null ? 'mpesa-instructions' : '']">
                <h5
                    v-html="instructions"
                    v-if="state.instruction || state.secondaryInstruction"
                    class="container__col-md-12 container__col-sm-12 payment__instruction"
                >
                </h5>
                
                <div class="center">
                    <div class="loader-icon"></div>
                </div>
            </div>
            <div v-if="state.fallbackDetails" class="account-box">
                <div class="account-box__field">
                    <label> {{$t("mpesa.label.amount")}} </label>
                    <h4>
                        {{ state.fallbackDetails.amount.currency }}
                        {{ state.fallbackDetails.amount.computed }}
                    </h4>
                </div>
                <div class="account-box__field">
                    <label>{{$t("mpesa.label.business")}}</label>
                    <h4>{{ state.fallbackDetails.business }}</h4>
                </div>
                <div class="account-box__field">
                    <label>{{$t("mpesa.label.account")}}</label>
                    <div class="account-copy">
                        <h4>
                            {{ state.fallbackDetails.account }}
                        </h4>
                        <copy-button :copyText="state.fallbackDetails.account" />
                    </div>
                </div>
            </div>
            <p class="payment__instruction--refresh">{{$t("ui.transaction.refresh")}}.</p>
        </div>
    </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { getValueFromPath } from "@/support/helpers";
import PrimarySelect from "@/components/form/base-select";
import PrimaryButton from "@/components/form/base-button";
import PrimaryInput from "@/components/form/base-input";
import CopyButton from "@/components/copy-button.vue";
import { injectStore, mapState } from "redux-vuex";
import { ref, onBeforeUnmount, computed, watch } from "vue";
import { isValidPhoneNumber } from "../../support/helpers";

export default {
    name: "Mpesa",
    components: {
        CopyButton,
        PrimaryInput,
        PrimaryButton,
        PrimarySelect,
    },
    setup(_, { emit }) {
        const store = injectStore();
        const checkoutData = ref(store.getState());
        const network = ref("");
        const telephone = ref("");
        const transactionSuccessful = ref(false);
        const momoError = ref(false);
        const errorMessage = ref("");
        const { t } = useI18n();

        const subscription = store.subscribe(() => {
            checkoutData.value = store.getState();
        });

        function checkPhoneAndNetwork() {
            if (network.value != "" && telephone.value != "") {
                let trimmedPhoneNumber = telephone.value.replace(/\s/g, "").replace(/[()]/g, "");

                if (isValidPhoneNumber(trimmedPhoneNumber)) {
                    momoError.value = `true-${new Date().getTime()}`;
                    errorMessage.value = "";
                    telephone.value = trimmedPhoneNumber;
                    step2();
                } 
                else {
                    momoError.value = `true-${new Date().getTime()}`;
                    errorMessage.value = t("mm.invalid.phonenumber");
                }
            }
        }

        function step2() {
            const data = { network: network.value, phone: telephone.value };
            checkoutData.value.view.context.$actions.update(data);
            setTimeout(() => {
                checkoutData.value.view.context.$actions.next();
            }, 0);
        }

        watch(momoError, (newVal, OldVal) => {
            if (newVal !== OldVal) {
                emit("momo-error", errorMessage.value);
            }
        });

        watch(() => checkoutData.value, (newData) => {
            if (newData) {
                momoError.value = false;
            }
        });

        const instructions = computed(() => {
            const instruction = [ checkoutData.value.view.context.instruction, "<br/> <br/>", checkoutData.value.view.context.secondaryInstruction].filter(Boolean).join(" ");
            return instruction;
        });
        onBeforeUnmount(() => {
            momoError.value = false;
            errorMessage.value = "";
            subscription();
        });

        return {
            state: mapState({
                currency: (state) =>
                    getValueFromPath(state, "currency.current") || null,
                amount: (state) => getValueFromPath(state, "amount") || null,
                processing: (state) => getValueFromPath(state, "processing") || false,
                defaultNetwork: (state) =>
                    getValueFromPath(state, "view.context.inputs.network.value"),
                networks: (state) =>
                    getValueFromPath(state, "view.context.inputs.network.options") || [],
                inputs: (state) => getValueFromPath(state, "view.context.inputs") || {},
                currentView: (state) => getValueFromPath(state, "view.current") || null,
                instruction: (state) =>
                    getValueFromPath(state, "view.context.instruction") || null,
                secondaryInstruction: (state) =>
                    getValueFromPath(state, "view.context.secondaryInstruction") || null,
                fallbackDetails: (state) =>
                    getValueFromPath(state, "view.context.fallbackDetails") || null,
            }),
            instructions,
            checkoutData,
            network,
            getValueFromPath,
            telephone,
            transactionSuccessful,
            checkPhoneAndNetwork,
            step2,
        };
    },
};
</script>

<style lang="scss" scoped>
.fallback-field {
  display: flex;
  justify-content: space-between;
  padding-bottom: 0.5rem;
  padding-left: 1rem;
  padding-right: 1rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #c4c4c4;
}
.mpesa-instructions {
  /* display: flex; */
  align-items: flex-start;
}

.loader-icon-small {
  width: 4rem;
  height: 4rem;
}
.account-box {
  margin-top: 1vh;
  margin-bottom: 1vh;
}
.payment__instruction {
  margin: 0 2rem 3.8rem;
  text-align: left;
  &--refresh {
    margin-top: 20px;
  }
}
</style>